import { API_URL } from './config.js';

/**
 * Telling the operator a screen crashed - C-32, pre-launch fixes.
 *
 * The error boundary used to log to the browser console, which nobody reads.
 * It now posts a small report to `POST /api/client-errors`, and so does a
 * listener for errors nothing else caught. The server logs it and sends the
 * operator at most one alert an hour.
 *
 * ## What goes, and what never does
 *
 * **Five things**: the page path, the message, the stack (trimmed), the build
 * id, and whether somebody was signed in, as yes or no. **No request bodies, no
 * form contents, no personal data, no cookies**: the request is sent without
 * credentials, so the server could not tell who it came from even if it tried.
 * The path is `location.pathname`, which leaves out the query string - that is
 * where a set-password or verification token lives. The server takes those out
 * again rather than trusting this file to.
 *
 * **Never throws, never waits.** Reporting a crash must not be able to cause
 * one, and a failure to report is silent.
 */

export interface ClientErrorReport {
  path: string;
  message: string;
  stack?: string;
  buildId?: string;
  signedIn: boolean;
}

/** The build this bundle came from, when the build knew. See `vite.config.ts`. */
export const BUILD_ID: string = import.meta.env.VITE_BUILD_ID ?? '';

/** Matches the server's own caps, so nothing is sent only to be cut. */
const MAX_MESSAGE = 500;
const MAX_STACK = 2_000;

/** At most this many reports from one page load. A crash loop is one problem. */
export const MAX_REPORTS_PER_PAGE = 3;

let signedIn = false;
let sent = 0;
const seen = new Set<string>();

/** Kept current by the session provider. Yes or no, and nothing about who. */
export function noteSignedIn(value: boolean): void {
  signedIn = value;
}

function describe(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      message: error.message || error.name || 'Error',
      ...(error.stack ? { stack: error.stack.slice(0, MAX_STACK) } : {}),
    };
  }
  if (typeof error === 'string') return { message: error };
  return { message: 'A value that was not an Error was thrown.' };
}

/** Builds the report. Exported for the tests, which assert what it carries. */
export function buildReport(error: unknown, path: string = window.location.pathname): ClientErrorReport {
  const { message, stack } = describe(error);
  return {
    path,
    message: message.slice(0, MAX_MESSAGE),
    ...(stack ? { stack } : {}),
    ...(BUILD_ID ? { buildId: BUILD_ID } : {}),
    signedIn,
  };
}

/** Sends one report, unless this page has already sent enough or this one. */
export function reportClientError(error: unknown): void {
  try {
    const report = buildReport(error);
    const key = `${report.path}|${report.message}`;
    if (sent >= MAX_REPORTS_PER_PAGE || seen.has(key)) return;
    sent += 1;
    seen.add(key);

    void fetch(`${API_URL}/api/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
      // No cookies: the report is anonymous by construction.
      credentials: 'omit',
      // Survives the page being reloaded, which is the button the person sees.
      keepalive: true,
    }).catch(() => {
      // Nothing to do. A report that cannot be sent is not worth a second error.
    });
  } catch {
    // As above: never the cause of a second failure.
  }
}

/**
 * Errors nothing else caught: a throw in an event handler, a promise nobody
 * awaited. Render errors go through the error boundary instead.
 *
 * "Script error." with no detail is what a browser reports for a script from
 * another origin - an extension, usually - and says nothing we could act on.
 */
export function installGlobalErrorReporting(target: Window = window): void {
  target.addEventListener('error', (event: ErrorEvent) => {
    if (!event.error && (!event.message || event.message === 'Script error.')) return;
    reportClientError(event.error ?? event.message);
  });
  target.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportClientError(event.reason);
  });
}

/** Test seam. */
export function resetClientErrorReporting(): void {
  signedIn = false;
  sent = 0;
  seen.clear();
}
